import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import paginateQuery from "../../helpers/paginate";
import { listCustomers } from "../../../../graphql/queries";
import { setSelectedCustomer } from "../../../state/selected-customer/selectedCustomerSlice";
import ControlledAutocompleteDsm2 from "../../../modules/Helpers/ControlledAutocompleteDsm2";
import featureFlags from "../../utils/featureFlags";

const OrganizationLoading = () => (
  <div className="flex h-10 flex-row items-center justify-center">
    <span className="text-body-link-regular text-black">
      navigation.topNav.organization.loading
    </span>
  </div>
);

type CustomerOption = {
  id: string;
  name: string;
};

export const OrganizationSwitcher = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedCustomer = useSelector(
    (state: {
      selectedCustomer?: { value?: { id?: string; name?: string } };
    }) => state.selectedCustomer?.value,
  );
  const [customers, setCustomers] = useState<CustomerOption[]>([]);

  useEffect(() => {
    let isMounted = true;

    const loadCustomers = async () => {
      try {
        const results = await paginateQuery<CustomerOption>(
          { query: listCustomers },
          "listCustomers",
        );
        if (isMounted) {
          const sortedCustomers = [...results].sort(
            (a: CustomerOption, b: CustomerOption) =>
              (a?.name ?? "").localeCompare(b?.name ?? ""),
          );
          setCustomers(sortedCustomers);
        }
      } catch (error) {
        console.error(
          "Failed to load customers for organization switcher",
          error,
        );
      }
    };

    void loadCustomers();

    return () => {
      isMounted = false;
    };
  }, []);

  const customerOptions = useMemo<string[]>(() => {
    const options = customers.map((customer) => customer.id);

    if (selectedCustomer?.id && !options.includes(selectedCustomer.id)) {
      options.unshift(selectedCustomer.id);
    }

    return options;
  }, [customers, selectedCustomer?.id]);

  if (!selectedCustomer) {
    return <OrganizationLoading />;
  }

  return (
    <ControlledAutocompleteDsm2
      name="selectedCustomerId"
      value={selectedCustomer.id}
      defaultValue={selectedCustomer.id}
      placeholder="Search customer"
      options={customerOptions}
      compact
      disablePopupIconFlip
      getOptionLabel={(customerId: string) =>
        customers.find((customer) => customer.id === customerId)?.name ??
        selectedCustomer.name ??
        ""
      }
      onChange={(nextCustomerId?: string | null) => {
        const nextCustomer = customers.find(
          (customer) => customer.id === nextCustomerId,
        );

        if (nextCustomer) {
          dispatch(setSelectedCustomer(nextCustomer));
          const homepagePath = featureFlags.homepageFP
            ? "/v2/homepage"
            : "/homepage";
          history.push(homepagePath);
        }
      }}
      style={{ minWidth: 220 }}
    />
  );
};
